import React from 'react';
import {
    DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors,
} from '@dnd-kit/core';
import {
    SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, Trash2, ExternalLink, AlertCircle } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { LinkIconPicker } from './LinkIconPicker';
import {
    DEFAULT_ICON_KEY, LINK_PRESETS, blankRow, guessIconKey, isValidUrl, normalizeUrl,
} from './linkMeta';
import { cn } from '../../lib/utils';

/**
 * ProjectLinksEditor — add, name, icon, reorder and remove project links.
 *
 * Order is the feature, not a nicety: the console's top bar surfaces the first
 * three links as icons and folds the rest into a menu, so dragging a row above
 * the third position is how you decide what is one click away. The editor draws
 * that boundary explicitly rather than making you infer it.
 *
 * Rows are keyed by a client-side uid from linkMeta, because the persisted link
 * shape has no id and an array index is not a stable key under reordering —
 * React would reconcile the wrong inputs mid-drag and text would jump rows.
 */

const HANDLE_HINT = 'Drag to reorder — the first three appear in the top bar';

function LinkRow({ row, index, onPatch, onRemove }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: row.uid });

    const urlTouched = !!row.url?.trim();
    const urlInvalid = urlTouched && !isValidUrl(row.url);

    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition }}
            className={cn(
                'flex items-start gap-2 rounded-2xl border border-white/5 bg-secondary/10 p-2 md:items-center',
                isDragging && 'z-10 border-primary/30 opacity-80 shadow-lg'
            )}
        >
            <button
                type="button"
                {...attributes}
                {...listeners}
                title={HANDLE_HINT}
                aria-label={`Reorder ${row.title || 'link'}`}
                className="mt-1 flex-shrink-0 cursor-grab touch-none rounded-lg p-1 text-muted-foreground/50 transition-colors hover:bg-white/5 hover:text-foreground active:cursor-grabbing md:mt-0"
            >
                <GripVertical className="h-4 w-4" />
            </button>

            <span className="mt-1.5 w-4 flex-shrink-0 text-center font-mono text-[10px] tabular-nums text-muted-foreground/50 md:mt-0">
                {index + 1}
            </span>

            <LinkIconPicker value={row.icon} onChange={(icon) => onPatch({ icon })} />


            <div className="flex min-w-0 flex-1 flex-col gap-2 md:flex-row">
                <Input
                    value={row.title}
                    onChange={(e) => onPatch({ title: e.target.value })}
                    placeholder="Label"
                    aria-label="Link label"
                    className="h-9 rounded-xl border-white/10 bg-black/20 md:w-44 md:flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                    <Input
                        value={row.url}
                        // Guessing on change rather than on blur means the icon
                        // settles as you finish typing the host, which reads as the
                        // field recognising the site.
                        //
                        // The icon only follows the URL while it is still whatever
                        // the URL produced. The moment it holds anything the guess
                        // wouldn't have picked, it's the user's choice and we stop
                        // touching it — which needs no "pinned" flag, and so still
                        // holds after a save and reload.
                        onChange={(e) => {
                            const url = e.target.value;
                            const derived =
                                !row.icon ||
                                row.icon === DEFAULT_ICON_KEY ||
                                row.icon === guessIconKey(row.url);
                            onPatch(derived ? { url, icon: guessIconKey(url) } : { url });
                        }}
                        placeholder="https://…"
                        aria-label="Link URL"
                        aria-invalid={urlInvalid}
                        className={cn(
                            'h-9 w-full rounded-xl border-white/10 bg-black/20 font-mono text-xs',
                            urlInvalid && 'border-destructive/50 focus-visible:ring-destructive/40'
                        )}
                    />
                    {urlInvalid ? (
                        <p className="mt-1 flex items-center gap-1 text-[10px] text-destructive">
                            <AlertCircle className="h-3 w-3" />
                            That doesn’t look like a valid address
                        </p>
                    ) : null}
                </div>
            </div>

            <div className="mt-0.5 flex flex-shrink-0 items-center gap-1 md:mt-0">
                <button
                    type="button"
                    disabled={!urlTouched || urlInvalid}
                    onClick={() => window.open(normalizeUrl(row.url), '_blank', 'noopener,noreferrer')}
                    title="Open in a new tab"
                    aria-label="Open link"
                    className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent"
                >
                    <ExternalLink className="h-3.5 w-3.5" />
                </button>
                <button
                    type="button"
                    onClick={onRemove}
                    title="Remove link"
                    aria-label={`Remove ${row.title || 'link'}`}
                    className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-destructive/20 hover:text-destructive"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
}

export function ProjectLinksEditor({ rows, onChange }) {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const patchRow = (uid, patch) =>
        onChange(rows.map((row) => (row.uid === uid ? { ...row, ...patch } : row)));

    const removeRow = (uid) => onChange(rows.filter((row) => row.uid !== uid));

    const addRow = (preset) => onChange([...rows, blankRow(preset)]);

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const from = rows.findIndex((row) => row.uid === active.id);
        const to = rows.findIndex((row) => row.uid === over.id);
        if (from === -1 || to === -1) return;
        onChange(arrayMove(rows, from, to));
    };

    const usedTypes = new Set(rows.map((row) => row.type));
    const availablePresets = LINK_PRESETS.filter((preset) => !usedTypes.has(preset.type));

    return (
        <div className="space-y-3">
            {rows.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-white/10 px-4 py-8 text-center">
                    <p className="text-sm text-muted-foreground">No links yet</p>
                    <p className="mt-1 text-xs text-muted-foreground/60">
                        Add the consoles, repos and docs you open most — the first three
                        become icons in the project bar.
                    </p>
                </div>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={rows.map((row) => row.uid)} strategy={verticalListSortingStrategy}>
                        <div className="space-y-2">
                            {rows.map((row, index) => (
                                <React.Fragment key={row.uid}>
                                    <LinkRow
                                        row={row}
                                        index={index}
                                        onPatch={(patch) => patchRow(row.uid, patch)}
                                        onRemove={() => removeRow(row.uid)}
                                    />
                                    {/* The bar's cut-off, drawn where it actually falls
                                        rather than explained in a caption nobody reads. */}
                                    {index === 2 && rows.length > 3 ? (
                                        <div className="flex items-center gap-3 py-1" aria-hidden>
                                            <div className="h-px flex-1 bg-gradient-to-r from-primary/40 to-transparent" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                                Below this: in the menu
                                            </span>
                                            <div className="h-px flex-1 bg-gradient-to-l from-primary/40 to-transparent" />
                                        </div>
                                    ) : null}
                                </React.Fragment>
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => addRow()}
                    data-testid="add-project-link"
                    className="h-9 rounded-xl border-white/10 hover:bg-white/5"
                >
                    <Plus className="mr-2 h-4 w-4" /> Add link
                </Button>

                {availablePresets.map((preset) => (
                    <button
                        key={preset.type}
                        type="button"
                        onClick={() => addRow(preset)}
                        className="flex h-9 items-center gap-1.5 rounded-xl border border-dashed border-white/10 px-3 text-xs text-muted-foreground transition-colors hover:border-primary/30 hover:bg-primary/5 hover:text-foreground"
                    >
                        <Plus className="h-3 w-3" />
                        {preset.title}
                    </button>
                ))}
            </div>
        </div>
    );
}

export default ProjectLinksEditor;
